import { Component } from "react";
import { StormcloudVideoPlayer } from "../player/StormcloudVideoPlayer";
import type { StormcloudVideoPlayerConfig } from "../types";
import { canPlay } from "../patterns";

export interface HlsPlayerProps extends StormcloudVideoPlayerConfig {
  onMount?: (player: any) => void;
  onReady?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onEnded?: () => void;
  onLoaded?: () => void;
  onError?: (error: any) => void;
}

export default class HlsPlayer extends Component<HlsPlayerProps> {
  static displayName = "HlsPlayer";

  static canPlay = canPlay.hls;

  private player: StormcloudVideoPlayer | null = null;
  private mounted = false;

  componentDidMount() {
    this.mounted = true;
    this.load();
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.player) {
      this.player.destroy();
      this.player = null;
    }
  }

  componentDidUpdate(prevProps: HlsPlayerProps) {
    if (prevProps.src !== this.props.src) {
      this.load();
    }
  }

  load = async () => {
    if (!this.props.videoElement || !this.props.src) return;

    try {
      if (this.player) {
        this.player.destroy();
        this.player = null;
      }

      const config: StormcloudVideoPlayerConfig = {
        src: this.props.src,
        videoElement: this.props.videoElement,
      };

      if (this.props.autoplay !== undefined)
        config.autoplay = this.props.autoplay;
      if (this.props.muted !== undefined) config.muted = this.props.muted;
      if (this.props.lowLatencyMode !== undefined)
        config.lowLatencyMode = this.props.lowLatencyMode;
      if (this.props.allowNativeHls !== undefined)
        config.allowNativeHls = this.props.allowNativeHls;
      if (this.props.isLiveStream !== undefined)
        config.isLiveStream = this.props.isLiveStream;
      if (this.props.driftToleranceMs !== undefined)
        config.driftToleranceMs = this.props.driftToleranceMs;
      if (this.props.immediateManifestAds !== undefined)
        config.immediateManifestAds = this.props.immediateManifestAds;
      if (this.props.debugAdTiming !== undefined)
        config.debugAdTiming = this.props.debugAdTiming;
      if (this.props.showCustomControls !== undefined)
        config.showCustomControls = this.props.showCustomControls;
      if (this.props.onVolumeToggle !== undefined)
        config.onVolumeToggle = this.props.onVolumeToggle;
      if (this.props.onFullscreenToggle !== undefined)
        config.onFullscreenToggle = this.props.onFullscreenToggle;
      if (this.props.onControlClick !== undefined)
        config.onControlClick = this.props.onControlClick;
      if (this.props.licenseKey !== undefined)
        config.licenseKey = this.props.licenseKey;
      if (this.props.adFailsafeTimeoutMs !== undefined)
        config.adFailsafeTimeoutMs = this.props.adFailsafeTimeoutMs;
      if (this.props.minSegmentsBeforePlay !== undefined)
        config.minSegmentsBeforePlay = this.props.minSegmentsBeforePlay;
      if (this.props.ctvAdRequest !== undefined)
        config.ctvAdRequest = this.props.ctvAdRequest;

      this.player = new StormcloudVideoPlayer(config);

      this.props.onMount?.(this);

      await this.player.load();

      if (this.mounted) {
        this.props.onReady?.();
      }
    } catch (error) {
      if (this.mounted) {
        this.props.onError?.(error);
      }
    }
  };

  play = () => {
    if (this.props.videoElement) {
      const video = this.props.videoElement;
      const hasValidSource =
        video.src ||
        (video.currentSrc && video.currentSrc !== "") ||
        video.readyState >= 1;

      if (hasValidSource) {
        video.play()?.catch((error) => {
          console.error("[HlsPlayer] Failed to play:", error);
          this.props.onError?.(error);
        });
        this.props.onPlay?.();
      } else {
        console.warn("[HlsPlayer] Cannot play: video has no valid source");
      }
    }
  };

  pause = () => {
    if (this.props.videoElement) {
      this.props.videoElement.pause();
      this.props.onPause?.();
    }
  };

  stop = () => {
    this.pause();
    if (this.props.videoElement) {
      this.props.videoElement.currentTime = 0;
    }
  };

  seekTo = (seconds: number, keepPlaying?: boolean) => {
    if (this.props.videoElement) {
      this.props.videoElement.currentTime = seconds;
      if (!keepPlaying) {
        this.pause();
      }
    }
  };

  setVolume = (volume: number) => {
    if (this.props.videoElement) {
      this.props.videoElement.volume = Math.max(0, Math.min(1, volume));
    }
  };

  mute = () => {
    if (this.props.videoElement) {
      this.props.videoElement.muted = true;
    }
  };

  unmute = () => {
    if (this.props.videoElement) {
      this.props.videoElement.muted = false;
    }
  };

  setPlaybackRate = (rate: number) => {
    if (this.props.videoElement && rate > 0) {
      this.props.videoElement.playbackRate = rate;
    }
  };

  getDuration = (): number | null => {
    if (this.props.videoElement && isFinite(this.props.videoElement.duration)) {
      return this.props.videoElement.duration;
    }
    return null;
  };

  getCurrentTime = (): number | null => {
    if (
      this.props.videoElement &&
      isFinite(this.props.videoElement.currentTime)
    ) {
      return this.props.videoElement.currentTime;
    }
    return null;
  };

  getSecondsLoaded = (): number | null => {
    if (
      this.props.videoElement &&
      this.props.videoElement.buffered.length > 0
    ) {
      return this.props.videoElement.buffered.end(
        this.props.videoElement.buffered.length - 1
      );
    }
    return null;
  };

  getInternalPlayer = (key = "player") => {
    if (key === "player") return this.player;
    if (key === "video") return this.props.videoElement;
    if (key === "hls" && this.player) return (this.player as any).hls;
    return null;
  };

  render() {
    return null;
  }
}
